import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/useAuth';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { ProfileIcon } from '../components/common/ProfileIcon';
import { uploadProfilePicture, deleteProfilePicture, getProfilePicture, getUserActivity } from '../api/users';
import { ErrorMessage } from '../components/common/ErrorMessage';
import { isImageFile, isValidImageSize } from '../utils/imageUtils';
import { ImageCropper } from '../components/common/ImageCropper';
import { PostCard } from '../components/post/PostCard';
import { CommentCard } from '../components/comment/CommentCard';
import type { UserActivity } from '../types/user';
import type { Comment } from '../types/comment';
import { useLanguage } from '../i18n/useLanguage';
import { useProfilePicture } from '../context/ProfilePictureContext';
import { ChangePassword } from '../components/profile/ChangePassword';
import { commentsApi } from '../api/comments';

export const ProfilePage: React.FC = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { bumpAvatarVersion } = useProfilePicture();
  const [uploading, setUploading] = useState(false);
  const [compressing] = useState(false);  // kept for UI consistency; compression now happens inside ImageCropper
  const [loadingPicture, setLoadingPicture] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [profilePictureUrl, setProfilePictureUrl] = useState<string | null>(null);
  const [cropperFile, setCropperFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Activity state
  const [activity, setActivity] = useState<UserActivity | null>(null);
  const [loadingActivity, setLoadingActivity] = useState(false);
  // Load profile picture on mount
  useEffect(() => {
    const loadProfilePicture = async () => {
      if (!user) {
        setLoadingPicture(false);
        return;
      }

      try {
        setLoadingPicture(true);
        const url = await getProfilePicture();
        setProfilePictureUrl(url);
      } catch (err: any) {
        console.error('Error loading profile picture:', err);
        // Don't show error for 404 - just means no picture is set
        if (err?.response?.status !== 404) {
          setError(t('profile.errorLoadPicture'));
        }
      } finally {
        setLoadingPicture(false);
      }
    };

    loadProfilePicture();
  }, [user]);

  // Load activity statistics
  useEffect(() => {
    const loadActivity = async () => {
      if (!user) return;

      try {
        setLoadingActivity(true);
        const activityData = await getUserActivity();
        setActivity(activityData);
      } catch (err: any) {
        console.error('Error loading activity:', err);
        // Don't show error for activity, just log it
      } finally {
        setLoadingActivity(false);
      }
    };

    loadActivity();
  }, [user]);

  // Step 1: file chosen → validate then open the cropper modal
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!isImageFile(file)) {
      setError(t('profile.errorNotImage'));
      return;
    }
    if (!isValidImageSize(file, 10 * 1024 * 1024)) {
      setError(t('profile.errorImageTooLarge'));
      return;
    }

    setError(null);
    setCropperFile(file);   // opens the cropper modal

    // Reset the input so the same file can be selected again later
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Step 2: user confirmed the crop → upload the result
  const handleCropConfirm = async (croppedFile: File) => {
    setCropperFile(null);
    setUploading(true);
    try {
      const url = await uploadProfilePicture(croppedFile);
      setProfilePictureUrl(`${url}?v=${Date.now()}`);
      bumpAvatarVersion();
    } catch (err) {
      console.error('Error uploading cropped profile picture:', err);
      setError(err instanceof Error ? err.message : t('profile.errorProcessPicture'));
    } finally {
      setUploading(false);
    }
  };

  const handleCropCancel = () => {
    setCropperFile(null);
  };

  const handleDeletePicture = async () => {
    if (!confirm(t('profile.confirmDeletePicture'))) {
      return;
    }

    setError(null);
    setUploading(true);

    try {
      await deleteProfilePicture();
      setProfilePictureUrl(null);
      bumpAvatarVersion();
    } catch (err) {
      console.error('Error deleting profile picture:', err);
      setError(err instanceof Error ? err.message : t('profile.errorDeletePicture'));
    } finally {
      setUploading(false);
    }
  };

  const refreshActivity = async () => {
    const activityData = await getUserActivity();
    setActivity(activityData);
  };

  const handleDeleteComment = async (comment: Comment) => {
    if (!window.confirm(t('comment.confirmDelete'))) {
      return;
    }
    try {
      await commentsApi.deleteComment(comment.id);
      await refreshActivity();
    } catch (err) {
      console.error('Error deleting comment:', err);
    }
  };


  if (loading) {
    return <LoadingSpinner size="large" />;
  }

  if (!user) {
    return <div>No user information available</div>;
  }

  return (
    <>
    {cropperFile && (
      <ImageCropper
        file={cropperFile}
        onConfirm={handleCropConfirm}
        onCancel={handleCropCancel}
      />
    )}
    <div className="profile-page">
      <h1>{t('profile.title')}</h1>
      <div className="profile-content">
        <div className="profile-picture-section">
          <div className="profile-picture-container">
            {loadingPicture ? (
              <div className="profile-picture-overlay">
                <LoadingSpinner size="small" />
              </div>
            ) : profilePictureUrl ? (
              <img
                src={profilePictureUrl}
                alt="Profile"
                className="profile-picture"
                onError={() => setProfilePictureUrl(null)}
              />
            ) : (
              <div className="profile-picture-placeholder">
                <ProfileIcon size={64} className="profile-picture-icon" />
              </div>
            )}
            {(uploading || compressing) && (
              <div className="profile-picture-overlay">
                <LoadingSpinner size="small" />
              </div>
            )}
          </div>
          <div className="profile-picture-actions">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
              id="profile-picture-input"
              disabled={uploading || compressing || loadingPicture}
            />
            <label htmlFor="profile-picture-input" className="button button--primary button--small" style={{ cursor: uploading || compressing || loadingPicture ? 'not-allowed' : 'pointer' }}>
              {compressing ? t('profile.compressing') : uploading ? t('profile.uploading') : loadingPicture ? t('common.loading') : profilePictureUrl ? t('profile.changePicture') : t('profile.uploadPicture')}
            </label>
            {profilePictureUrl && !loadingPicture && (
              <button
                onClick={handleDeletePicture}
                className="button button--danger button--small"
                disabled={uploading || compressing || loadingPicture}
              >
                {t('profile.deletePicture')}
              </button>
            )}
          </div>
          {error && (
            <ErrorMessage
              message={error}
              onDismiss={() => setError(null)}
            />
          )}
        </div>
        <div className="profile-section">
          <h2>{t('profile.accountInfo')}</h2>
          <div className="profile-field">
            <label>{t('profile.username')}</label>
            <span>{user.preferred_username || user.email || t('profile.na')}</span>
          </div>
          {user.email && (
            <div className="profile-field">
              <label>{t('profile.email')}</label>
              <span>{user.email}</span>
            </div>
          )}
          {user.name && (
            <div className="profile-field">
              <label>{t('profile.fullName')}</label>
              <span>{user.name}</span>
            </div>
          )}
          {user.given_name && (
            <div className="profile-field">
              <label>{t('profile.firstName')}</label>
              <span>{user.given_name}</span>
            </div>
          )}
          {user.family_name && (
            <div className="profile-field">
              <label>{t('profile.lastName')}</label>
              <span>{user.family_name}</span>
            </div>
          )}
        </div>

        <ChangePassword />

        {/* Your Activity Section */}
        <div className="profile-activity-section">
          <h2>{t('profile.activity')}</h2>

          {loadingActivity ? (
            <LoadingSpinner size="medium" />
          ) : activity ? (
            <>
              {/* Posting Activity */}
              <div className="activity-subsection">
                <h3>{t('profile.postingActivity')}</h3>
                <div className="activity-stats">
                  <div className="activity-stat">
                    <span className="activity-stat-label">{t('profile.totalPosts')}</span>
                    <span className="activity-stat-value">{activity.postingActivity.totalPosts}</span>
                  </div>
                </div>
                {activity.postingActivity.recentPosts.length > 0 && (
                  <div className="activity-recent">
                    <h4>{t('profile.recentPosts')}</h4>
                    <div className="activity-recent-list">
                      {activity.postingActivity.recentPosts.map((post) => (
                        <div key={post.id} className="activity-recent-item">
                          <PostCard post={post} onRefreshList={refreshActivity} />
                        </div>
                      ))}
                    </div>
                    {activity.postingActivity.totalPosts > activity.postingActivity.recentPosts.length && (
                      <div className="activity-show-all">
                        <button
                          className="button button--outline button--small"
                          onClick={() => navigate('/')}
                        >
                          {t('profile.showAllPosts').replace('{count}', String(activity.postingActivity.totalPosts))}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Commenting Activity */}
              <div className="activity-subsection">
                <h3>{t('profile.commentingActivity')}</h3>
                <div className="activity-stats">
                  <div className="activity-stat">
                    <span className="activity-stat-label">{t('profile.totalComments')}</span>
                    <span className="activity-stat-value">{activity.commentingActivity.totalComments}</span>
                  </div>
                </div>
                {activity.commentingActivity.recentComments.length > 0 && (
                  <div className="activity-recent">
                    <h4>{t('profile.recentComments')}</h4>
                    <div className="activity-recent-list">
                      {activity.commentingActivity.recentComments.map((comment) => (
                        <div key={comment.id} className="activity-recent-item">
                          <CommentCard
                            comment={comment}
                            bottomBar={
                              <div className="comment-card-profile-actions">
                                <button
                                  type="button"
                                  className="button button--outline button--small"
                                  onClick={() => navigate(`/posts/${comment.postId}`)}
                                >
                                  {t('profile.viewPost')}
                                </button>
                                <button
                                  type="button"
                                  className="delete-post-link delete-post-link--labeled"
                                  onClick={() => void handleDeleteComment(comment)}
                                  aria-label={t('post.actionDelete')}
                                  title={t('post.actionDelete')}
                                >
                                  <svg
                                    className="delete-post-icon"
                                    viewBox="0 0 24 24"
                                    width="18"
                                    height="18"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    aria-hidden
                                  >
                                    <polyline points="3 6 5 6 21 6" />
                                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                    <line x1="10" y1="11" x2="10" y2="17" />
                                    <line x1="14" y1="11" x2="14" y2="17" />
                                  </svg>
                                  <span>{t('post.actionDelete')}</span>
                                </button>
                              </div>
                            }
                          />
                        </div>
                      ))}
                    </div>
                    {activity.commentingActivity.totalComments > activity.commentingActivity.recentComments.length && (
                      <div className="activity-show-all">
                        <button
                          className="button button--outline button--small"
                          onClick={() => navigate('/')}
                        >
                          {t('profile.showAllComments').replace('{count}', String(activity.commentingActivity.totalComments))}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Statistics */}
              <div className="activity-subsection">
                <h3>{t('profile.statistics')}</h3>
                <div className="activity-stats">
                  <div className="activity-stat">
                    <span className="activity-stat-label">{t('profile.totalActivity')}</span>
                    <span className="activity-stat-value">{activity.statistics.totalActivity}</span>
                  </div>
                </div>
                <div className="activity-recent-activity">
                  <p>{activity.statistics.recentActivity}</p>
                </div>
              </div>
            </>
          ) : (
            <p>{t('profile.noActivity')}</p>
          )}
        </div>
      </div>
    </div>
    </>
  );
};

